import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, User, ShoppingCart, Menu, X } from "lucide-react";
import { BRAND } from "../lib/constants";
import { useCart } from "../context/CartContext";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { resolveImg } from "../lib/api";

const navItems = [
  { to: "/", label: "Home", testid: "nav-home", end: true },
  { to: "/shop", label: "Shop", testid: "nav-shop" },
  { to: "/categories", label: "Categories", testid: "nav-categories" },
];

const Header = () => {
  const { count } = useCart();
  const { settings } = useSiteSettings();
  const { customer } = useCustomerAuth();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  const logo = settings?.logo_url || BRAND.logo;

  const submitSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/shop?search=${encodeURIComponent(query.trim())}`);
      setSearchOpen(false);
      setQuery("");
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-[#F5F0E6]/95 backdrop-blur-md border-b border-[#E2DAD0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          {/* Logo + brand */}
          <Link to="/" className="flex items-center gap-4 shrink-0" data-testid="header-logo">
            <img
              src={resolveImg(logo)}
              alt="Aarmira Supernaturals"
              className="h-16 w-16 sm:h-20 sm:w-20 rounded-full object-cover ring-2 ring-[#C5A059] bg-white"
            />
            <div className="leading-tight">
              <div className="font-serif font-bold text-2xl sm:text-3xl lg:text-4xl text-[#1F4D2E] tracking-tight">
                Aarmira Supernaturals
              </div>
              <div className="text-sm sm:text-base font-semibold tracking-wide text-[#846321]">
                {BRAND.tagline}
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-10">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `relative text-[15px] font-medium transition-colors pb-1 ${
                    isActive
                      ? "text-[#1F4D2E] font-semibold border-b-2 border-[#1F4D2E]"
                      : "text-[#5A6B5D] hover:text-[#1F4D2E]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={() => setSearchOpen((s) => !s)}
              className="p-2 text-[#1C2B20] hover:text-[#1F4D2E] transition-colors"
              data-testid="header-search-btn"
              aria-label="Search"
            >
              <Search size={21} />
            </button>
            <Link
              to={customer ? "/account" : "/login"}
              className="relative p-2 text-[#1C2B20] hover:text-[#1F4D2E] transition-colors"
              data-testid="header-account-btn"
              aria-label="Account"
            >
              <User size={21} />
              {customer && (
                <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-[#1F4D2E] ring-2 ring-[#F5F0E6]" />
              )}
            </Link>
            <Link
              to="/cart"
              className="relative p-2 text-[#1C2B20] hover:text-[#1F4D2E] transition-colors"
              data-testid="header-cart-btn"
              aria-label="Cart"
            >
              <ShoppingCart size={21} />
              {count > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 bg-[#1F4D2E] text-[#F5F0E6] text-[10px] font-bold h-5 w-5 flex items-center justify-center rounded-full"
                  data-testid="cart-count-badge"
                >
                  {count}
                </span>
              )}
            </Link>
            <button
              onClick={() => setMobileOpen((m) => !m)}
              className="md:hidden p-2 text-[#1C2B20]"
              aria-label="Menu"
              data-testid="mobile-menu-btn"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Search bar */}
        {searchOpen && (
          <form onSubmit={submitSearch} className="pb-4 animate-fade-up">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for juices, soaps, supplements..."
              className="w-full rounded-full border border-[#E2DAD0] bg-white px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4D2E]"
              data-testid="header-search-input"
            />
          </form>
        )}

        {/* Mobile nav */}
        {mobileOpen && (
          <nav className="md:hidden pb-4 flex flex-col gap-1 animate-fade-up">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-[15px] font-medium ${
                    isActive ? "bg-[#1F4D2E] text-white" : "text-[#1C2B20]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <NavLink
              to={customer ? "/account" : "/login"}
              onClick={() => setMobileOpen(false)}
              className="px-3 py-2 rounded-lg text-[15px] font-medium text-[#1C2B20]"
            >
              {customer ? "My Account" : "Login / Register"}
            </NavLink>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
